import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BookmarkPlus, Trash2, Share2, Download, FileText, Crown, Lock, ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { toast } from 'sonner';
import { UpgradePrompt } from '../upgrade/UpgradePrompt';
import { saveTemplateOffline, getAllTemplatesOffline, deleteTemplateOffline } from '@/utils/offlineStorage';

interface Template {
  id: string;
  name: string;
  trip_type: string;
  destination_type?: string;
  items: any;
  is_public: boolean;
  created_at?: string;
}

interface TemplateManagerProps {
  currentCategories?: any[];
  currentTripType?: string;
  onLoadTemplate?: (template: Template) => void;
}

export function TemplateManager({ currentCategories, currentTripType, onLoadTemplate }: TemplateManagerProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [offlineTemplates, setOfflineTemplates] = useState<any[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const { hasFeature } = useSubscription();

  useEffect(() => {
    if (user && hasFeature('custom_templates')) {
      loadTemplates();
      loadOfflineTemplates();
    }
  }, [user, hasFeature]);

  const loadTemplates = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('packing_templates')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadOfflineTemplates = async () => {
    const offline = await getAllTemplatesOffline();
    setOfflineTemplates(offline);
  };

  const saveTemplate = async () => {
    if (!templateName.trim() || !currentCategories || !currentTripType) {
      toast.error('Please provide a template name');
      return;
    }

    setLoading(true);

    try {
      // Convert categories to items format
      const itemsToSave = currentCategories.reduce((acc, category) => {
        acc[category.id] = category.items.map((item: any) => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity || 1,
          essential: item.essential,
        }));
        return acc;
      }, {} as any);

      const templateData = {
        name: templateName.trim(),
        trip_type: mapTripTypeToDb(currentTripType) as 'adventure' | 'business' | 'family' | 'leisure' | 'romantic' | 'solo',
        items: itemsToSave,
        is_public: isPublic,
      };

      // Save to database
      const { data, error } = await supabase
        .from('packing_templates')
        .insert([templateData])
        .select()
        .single();

      if (error) throw error;

      // Also save offline for First Class users
      if (hasFeature('offline_mode') && data) {
        await saveTemplateOffline(data.id, templateData.name, templateData.trip_type, templateData.items);
      }

      toast.success(`Template "${templateName}" saved successfully!`);
      setShowSaveDialog(false);
      setTemplateName('');
      setIsPublic(false);
      loadTemplates();
      loadOfflineTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (id: string, name: string) => {
    if (!confirm(`Delete template "${name}"?`)) return;

    try {
      const { error } = await supabase.from('packing_templates').delete().eq('id', id);

      if (error) throw error;

      // Also delete from offline storage
      await deleteTemplateOffline(id);

      toast.success('Template deleted');
      loadTemplates();
      loadOfflineTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const mapTripTypeToDb = (type: string) => {
    const typeMap: { [key: string]: string } = {
      business: 'business',
      leisure: 'leisure',
      adventure: 'adventure',
      family: 'family',
      romantic: 'romantic',
      solo: 'solo',
      beach: 'leisure',
      city: 'leisure',
      cultural: 'leisure',
    };
    return typeMap[type.toLowerCase()] || 'leisure';
  };

  const getTripTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      business: 'Business',
      leisure: 'Leisure',
      adventure: 'Adventure',
      family: 'Family',
      romantic: 'Romantic',
      solo: 'Solo',
    };
    return labels[type] || type;
  };

  const isLocked = !hasFeature('custom_templates');

  return (
    <Card className="border-cyan-500/20 shadow-md shadow-cyan-500/5 overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-3 cursor-pointer transition-colors bg-gradient-to-br from-cyan-50/60 to-transparent dark:from-cyan-950/15 dark:to-transparent hover:from-cyan-50 dark:hover:from-cyan-950/25">
            <CardTitle className="flex items-center gap-3 text-base sm:text-lg">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-sm shadow-cyan-500/20">
                <FileText className="h-4 w-4" />
              </span>
              My Templates
              {isLocked && <Lock className="h-4 w-4 text-muted-foreground" />}
              {isOpen ? (
                <ChevronDown className="h-4 w-4 ml-auto text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
              )}
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm pl-11">Save and reuse your favorite packing lists</CardDescription>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent>
            {isLocked ? (
              <UpgradePrompt
                feature="custom_templates"
                requiredTier="first_class"
                title="Save and Reuse Your Packing Lists"
                description="Create custom templates for your favorite trips"
                benefits={[
                  "Save packing lists as reusable templates",
                  "Build a template library for common trip types",
                  "Share templates with friends and family"
                ]}
              >
                <div />
              </UpgradePrompt>
            ) : (
              <>
                {currentCategories && (
                  <Button onClick={() => setShowSaveDialog(true)} className="gap-2 mb-4 w-full">
                    <BookmarkPlus className="h-4 w-4" />
                    Save as Template
                  </Button>
                )}
                {templates.length === 0 && offlineTemplates.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No templates saved yet</p>
                    <p className="text-xs mt-1">Create a packing list and save it as a template</p>
                  </div>
                ) : (
                  <div className="grid gap-2">
                    {templates.map((template) => (
                      <div key={template.id} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-semibold text-sm truncate">{template.name}</h3>
                              <Badge variant="outline" className="text-xs">{getTripTypeLabel(template.trip_type)}</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {Object.keys(template.items).length} categories
                            </p>
                          </div>
                          <div className="flex gap-1">
                            {onLoadTemplate && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onLoadTemplate(template)}
                              >
                                Load
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteTemplate(template.id, template.name)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>

      {/* Save Template Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-yellow-500" />
              Save as Template
            </DialogTitle>
            <DialogDescription>
              Save this packing list as a reusable template for future trips
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                placeholder="e.g., Beach Vacation, Business Trip, Winter Sports"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </div>
            {hasFeature('share_templates') && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is-public"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="is-public" className="text-sm">
                  Share with travel group (they can view and use this template)
                </Label>
              </div>
            )}
            {hasFeature('offline_mode') && (
              <div className="bg-primary/10 p-3 rounded-lg">
                <p className="text-sm flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Template will be available offline
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveTemplate} disabled={loading}>
              {loading ? 'Saving...' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}